import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail } from 'lucide-react';
import { formatAddress, formatAddressShort } from '@/features/storefront/utils/addressFormat';
import type { RestaurantResponse } from '@/features/restaurants/types';

interface LandingContactSectionProps {
  restaurant: RestaurantResponse;
}

export function LandingContactSection({ restaurant }: LandingContactSectionProps) {
  const { t } = useTranslation();

  return (
    <section id="contact" className="py-10 sm:py-12 md:py-16 border-t border-border/50 bg-muted/20 scroll-mt-24">
      <div className="max-w-4xl mx-auto px-3 sm:px-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center text-foreground tracking-tight"
        >
          {t('public.contact')} & {t('landing.location', 'Location')}
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row flex-wrap justify-center gap-6 sm:gap-8"
        >
          {(restaurant.street || restaurant.city) && (
            <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto">
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-foreground" aria-hidden />
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <p className="font-medium text-foreground mb-0.5">{t('landing.address', 'Address')}</p>
                <a
                  href={restaurant.latitude != null && restaurant.longitude != null
                    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([restaurant.name, `${restaurant.latitude},${restaurant.longitude}`].filter(Boolean).join(', '))}`
                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([restaurant.name, formatAddress(restaurant)].filter(Boolean).join(', '))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors truncate block"
                  aria-label={t('landing.openInGoogleMaps', 'Open address in Google Maps')}
                >
                  <span className="sm:hidden">{formatAddressShort(restaurant)}</span>
                  <span className="hidden sm:inline">{formatAddress(restaurant)}</span>
                </a>
              </div>
            </div>
          )}
          {restaurant.contactPhone && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 text-foreground" />
              </div>
              <div>
                <p className="font-medium text-foreground mb-0.5">{t('landing.call')}</p>
                <a href={`tel:${restaurant.contactPhone}`} className="text-muted-foreground hover:text-foreground transition-colors">
                  {restaurant.contactPhone}
                </a>
              </div>
            </div>
          )}
          {restaurant.contactEmail && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-foreground" />
              </div>
              <div>
                <p className="font-medium text-foreground mb-0.5">{t('landing.email', 'Email')}</p>
                <a href={`mailto:${restaurant.contactEmail}`} className="text-muted-foreground hover:text-foreground transition-colors">
                  {restaurant.contactEmail}
                </a>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
